import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { usersSearchQuery } from "../queries";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 350;

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * TanStack Query-backed user search hook for the
 * `GET .../features/access/users/search?q=<query>` endpoint.
 *
 * Exposes `setQuery` to drive the search term and `clear` to reset.
 * The query is debounced so the request only fires once the user
 * stops typing, avoiding excessive Auth0 API calls.
 */
export function useSearchUsers() {
  const apiConfig = useApiConfig();
  const [query, setQuery] = useState("");

  const debouncedQuery = useDebouncedValue(query.trim(), DEBOUNCE_MS);
  const isEnabled = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching } = useQuery({
    ...usersSearchQuery(apiConfig, {}, { q: debouncedQuery }),
    enabled: isEnabled,
  });

  const clear = useCallback(() => {
    setQuery("");
  }, []);

  return {
    results: isEnabled ? (data?.users ?? []) : [],
    isSearching: isFetching,
    query,
    setQuery,
    clear,
  };
}
